<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import CartPanel from 'cart/components/CartPanel.vue';
import BasePaywallModal from 'dashboard/routes/dashboard/settings/components/BasePaywallModal.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import wootConstants from 'dashboard/constants/globals';

defineProps({
  currentChat: {
    required: true,
    type: Object,
  },
});

const { uiSettings, updateUISettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const router = useRouter();
const { currentAccount, accountId, isOnChatwootCloud } = useAccount();
const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const isCheckoutEnabled = computed(
  () =>
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CART
    ) &&
    (currentAccount.value?.settings?.checkout_enabled ?? false)
);
const paywallI18nKey = computed(() =>
  isOnChatwootCloud.value ? 'PAYWALL' : 'ENTERPRISE_PAYWALL'
);

const openBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};

const activeTab = computed(() => {
  const {
    is_contact_sidebar_open: isContactSidebarOpen,
    is_cart_panel_open: isCartPanelOpen,
  } = uiSettings.value;

  if (isContactSidebarOpen) return 0;
  if (isCartPanelOpen) return 2;
  return null;
});

const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);

const closeContactPanel = () => {
  if (
    isSmallScreen.value &&
    (uiSettings.value?.is_contact_sidebar_open ||
      uiSettings.value?.is_cart_panel_open)
  ) {
    updateUISettings({
      is_contact_sidebar_open: false,
      is_copilot_panel_open: false,
      is_cart_panel_open: false,
    });
  }
};
</script>

<template>
  <div
    v-on-click-outside="() => closeContactPanel()"
    class="bg-n-surface-2 h-full overflow-hidden flex flex-col fixed top-0 z-40 w-full max-w-sm transition-transform duration-300 ease-in-out ltr:right-0 rtl:left-0 md:static md:w-[320px] md:min-w-[320px] ltr:border-l rtl:border-r border-n-weak 2xl:min-w-[360px] 2xl:w-[360px] shadow-lg md:shadow-none"
    :class="[
      {
        'md:flex': activeTab === 0 || activeTab === 2,
        'md:hidden': activeTab !== 0 && activeTab !== 2,
      },
    ]"
  >
    <div class="flex flex-1 overflow-auto">
      <ContactPanel
        v-show="activeTab === 0"
        :conversation-id="currentChat.id"
        :inbox-id="currentChat.inbox_id"
      />
      <template v-if="activeTab === 2">
        <CartPanel
          v-if="isCheckoutEnabled"
          :conversation-id="currentChat.id"
          :contact-id="currentChat.meta?.sender?.id"
        />
        <div v-else class="flex w-full items-center justify-center p-6">
          <BasePaywallModal
            feature-prefix="CHECKOUT_SETTINGS"
            :i18n-key="paywallI18nKey"
            :is-on-chatwoot-cloud="isOnChatwootCloud"
            class="!max-w-none !shadow-none !border-0 !px-0 !py-0"
            @upgrade="openBilling"
          />
        </div>
      </template>
    </div>
  </div>
</template>
